<template>
  <AutocompleteField
    v-if="isFieldVisible(name) && isSchoolSearchAllowed"
    v-model="org_id"
    label-key="text"
    value-key="id"
    :filterable="false"
    :id="id"
    :label="label ?? $t('form.field.school.label')"
    :fetching="isFetching"
    :loading="isLoading"
    :name="name"
    :options="options"
    :placeholder="$t('form.field.school.placeholder')"
    :required="required"
    @update:query="onSearch"
    @scroll-end="fetchNextOptions()"
  >
    <template #option="{ option }">
      <div class="d-flex justify-content-between overflow-hidden">
        <div class="pe-2" style="white-space: normal">
          {{ (option as SchoolSearchItem).name }}
        </div>
        <div class="font-weight-bold">
          {{ (option as SchoolSearchItem).postcode }}
        </div>
      </div>
    </template>

    <template #list-footer>
      <li
        v-show="query.length > 1"
        class="text-center p-2"
        style="background-color: #ebe8ec"
      >
        <a href="/contact"> School not found? </a>
      </li>
    </template>
  </AutocompleteField>
</template>

<script lang="ts">
export type SchoolFieldProps = {
  id?: string;
  label?: string;
  name?: string;
  required?: boolean;
};
</script>

<script setup lang="ts">
import { useContactCaptureContext } from "@/components/forms/useContactCapture";
import {
  SchoolSearchItem,
  SchoolSearchResult,
  searchSchool,
} from "@/lib/searchSchool";
import { debounce } from "underscore";
import { computed, ref } from "vue";
import AutocompleteField from "./AutocompleteField.vue";

const minQuerySearch = 2;

withDefaults(defineProps<SchoolFieldProps>(), {
  name: "school-field",
});

const {
  org_id,
  country,
  isSchoolSearchAllowed,
  isFieldVisible,
} = useContactCaptureContext();

const query = ref<string>("");
const isFetching = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const options = ref<SchoolSearchItem[]>([]);

// Pagination
const skip = ref<number>(0);
const limit = ref<number>(50);
const total = ref<number>(0);

const hasNextPage = computed<boolean>(
  () => skip.value + limit.value < total.value
);

/**
 * Callback for VueSelect `search` event.
 *
 * @param search - Current search text.
 */
function onSearch(search: string) {
  query.value = search;
  skip.value = 0;

  if (query.value.length >= minQuerySearch) {
    options.value = [];
    org_id.value = null;
    isLoading.value = true;
    startSearch();
  }
}

/**
 * Debounce schools fetching.
 */
const startSearch = debounce(async () => {
  isLoading.value = true;

  let result: SchoolSearchResult;

  try {
    result = await fetchSchools();
  } catch (error) {
    console.error("Error while searching for schools.", error);
    return;
  } finally {
    isLoading.value = false;
  }

  options.value = result.hits;
  total.value = result.total;
}, 500);

/**
 * Fetch the next page of options.
 */
async function fetchNextOptions(): Promise<void> {
  if (!hasNextPage.value) {
    return;
  }

  skip.value += limit.value;

  let result: SchoolSearchResult;

  isFetching.value = true;

  try {
    result = await fetchSchools();
  } catch (error) {
    console.error("Error while fetching next page.", error);
    return;
  } finally {
    isFetching.value = false;
  }

  options.value = [...options.value, ...result.hits];
  total.value = result.total;
}

/**
 * Fetch the server for schools and return the result.
 */
async function fetchSchools(): Promise<SchoolSearchResult> {
  let result: SchoolSearchResult;

  result = await searchSchool({
    term: query.value,
    from: skip.value,
    size: limit.value,
    countryId: country.value ?? 1,
  });

  // compact UK country IDs in the response.
  const ukCountryIds = [1, 2, 3, 4, 10];

  result.hits = result.hits.map((option) => {
    return {
      ...option,
      countryId: ukCountryIds.includes(option.countryId) ? 1 : option.countryId,
    };
  });

  return result;
}

/**
 *
 */
/*function enterManually() {
  org_id.value = null;
  reportSchoolNotFound(query.value);

  manualSchoolEntry.value = true;
  organisation.value = query.value;
}*/
</script>
